import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { History, Star, Sparkles, Bug, Wrench, Plus, Trash2, X, Pencil, Send, Save, Eye, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";

const getTypeIcon = (type) => {
  switch (type) {
    case "feature":
      return <Sparkles className="w-4 h-4 text-blue-500" />;
    case "fix":
      return <Bug className="w-4 h-4 text-red-500" />;
    case "improvement":
      return <Wrench className="w-4 h-4 text-amber-500" />;
    default:
      return <Star className="w-4 h-4 text-slate-500" />;
  }
};

const getTypeBadge = (type) => {
  switch (type) {
    case "release":
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Major Release</Badge>;
    case "minor":
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Minor Update</Badge>;
    case "patch":
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Patch</Badge>;
    default:
      return <Badge variant="outline">Update</Badge>;
  }
};

function VersionForm({ data, onChange, onSubmit, onCancel, submitLabel, submitIcon: SubmitIcon }) {
  const addChange = () => {
    onChange({ ...data, changes: [...data.changes, { type: "feature", text: "" }] });
  };

  const removeChange = (index) => {
    onChange({ ...data, changes: data.changes.filter((_, i) => i !== index) });
  };

  const updateChange = (index, field, value) => {
    onChange({
      ...data,
      changes: data.changes.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    });
  };

  const moveChange = (index, direction) => {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= data.changes.length) return;
    const reordered = [...data.changes];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    onChange({ ...data, changes: reordered });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Version Number</Label>
          <Input
            placeholder="e.g., 1.1.0"
            value={data.version}
            onChange={(e) => onChange({ ...data, version: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Release Type</Label>
          <Select value={data.type} onValueChange={(v) => onChange({ ...data, type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="release">Major Release</SelectItem>
              <SelectItem value="minor">Minor Update</SelectItem>
              <SelectItem value="patch">Patch</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Title</Label>
        <Input
          placeholder="e.g., New Features & Bug Fixes"
          value={data.title}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Changes</Label>
          <Button type="button" variant="outline" size="sm" onClick={addChange}>
            <Plus className="w-4 h-4 mr-1" /> Add Change
          </Button>
        </div>

        {data.changes.map((change, index) => (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex flex-col gap-0.5 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-slate-400 hover:text-slate-700"
                disabled={index === 0}
                onClick={() => moveChange(index, "up")}
              >
                <ArrowUp className="w-3 h-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-slate-400 hover:text-slate-700"
                disabled={index === data.changes.length - 1}
                onClick={() => moveChange(index, "down")}
              >
                <ArrowDown className="w-3 h-3" />
              </Button>
            </div>
            <Select value={change.type} onValueChange={(v) => updateChange(index, "type", v)}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feature">
                  <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-500" /> Feature</span>
                </SelectItem>
                <SelectItem value="fix">
                  <span className="flex items-center gap-2"><Bug className="w-4 h-4 text-red-500" /> Bug Fix</span>
                </SelectItem>
                <SelectItem value="improvement">
                  <span className="flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-500" /> Improvement</span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Describe the change..."
              value={change.text}
              onChange={(e) => updateChange(index, "text", e.target.value)}
              className="flex-1 min-h-[40px] resize-none"
              rows={1}
            />
            {data.changes.length > 1 && (
              <Button type="button" variant="ghost" size="icon" onClick={() => removeChange(index)} className="text-slate-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        )}
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
          {SubmitIcon && <SubmitIcon className="w-4 h-4 mr-2" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export default function Versions({ user }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [versionToPublish, setVersionToPublish] = useState(null);

  const emptyForm = { version: "", title: "", type: "minor", changes: [{ type: "feature", text: "" }] };
  const [newVersion, setNewVersion] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const isTeacher = user.role === "teacher";

  useEffect(() => { fetchVersions(); }, []);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/versions`);
      setVersions(response.data);
    } catch (error) {
      console.error("Error fetching versions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newVersion.version || !newVersion.title) { toast.error("Please fill in version number and title"); return; }
    const validChanges = newVersion.changes.filter((c) => c.text.trim());
    if (validChanges.length === 0) { toast.error("Please add at least one change"); return; }

    try {
      await axios.post(`${API}/versions`, { ...newVersion, changes: validChanges });
      await fetchVersions();
      setNewVersion(emptyForm);
      setDialogOpen(false);
      toast.success("Draft created — edit anytime, then publish when ready");
    } catch (error) {
      toast.error("Failed to create version");
    }
  };

  const startEdit = (release) => {
    setEditingId(release.version_id);
    setEditForm({
      version: release.version,
      title: release.title,
      type: release.type,
      changes: release.changes && release.changes.length > 0 ? release.changes : [{ type: "feature", text: "" }],
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.version || !editForm.title) { toast.error("Please fill in version number and title"); return; }
    const validChanges = editForm.changes.filter((c) => c.text.trim());
    if (validChanges.length === 0) { toast.error("Please add at least one change"); return; }

    try {
      await axios.put(`${API}/versions/${editingId}`, { ...editForm, changes: validChanges });
      await fetchVersions();
      setEditingId(null);
      toast.success("Draft saved");
    } catch (error) {
      toast.error("Failed to save changes");
    }
  };

  const handlePublish = async () => {
    if (!versionToPublish) return;
    try {
      await axios.put(`${API}/versions/${versionToPublish}/publish`);
      await fetchVersions();
      toast.success("Version published and is now live!");
    } catch (error) {
      toast.error("Failed to publish version");
    } finally {
      setVersionToPublish(null);
      setPublishDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!versionToDelete) return;
    try {
      await axios.delete(`${API}/versions/${versionToDelete}`);
      setVersions((prev) => prev.filter((v) => v.version_id !== versionToDelete));
      toast.success("Version deleted");
    } catch (error) {
      toast.error("Failed to delete version");
    } finally {
      setVersionToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const moveVersion = async (versionId, direction) => {
    const idx = versions.findIndex((v) => v.version_id === versionId);
    if (idx < 0) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= versions.length) return;

    const reordered = [...versions];
    [reordered[idx], reordered[targetIdx]] = [reordered[targetIdx], reordered[idx]];
    setVersions(reordered);

    try {
      await axios.put(`${API}/versions/reorder`, {
        version_ids: reordered.map((v) => v.version_id),
      });
    } catch (error) {
      toast.error("Failed to reorder");
      fetchVersions();
    }
  };

  const drafts = versions.filter((v) => v.status === "draft");
  const published = versions.filter((v) => v.status === "published");
  const latestPublished = published.length > 0 ? published[0] : null;

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <History className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold text-slate-900">Version History</h1>
              <p className="text-slate-600">Track updates and improvements to the application</p>
            </div>
          </div>

          {isTeacher && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> New Draft
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Version Draft</DialogTitle>
                  <DialogDescription>
                    Start a new version post as a draft. You can edit it anytime and publish when ready.
                  </DialogDescription>
                </DialogHeader>
                <VersionForm
                  data={newVersion}
                  onChange={setNewVersion}
                  onSubmit={handleCreate}
                  onCancel={() => setDialogOpen(false)}
                  submitLabel="Save as Draft"
                  submitIcon={Save}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Current Version Banner */}
        {latestPublished && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Current Version</span>
                </div>
                <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-lg px-4 py-1">
                  v{latestPublished.version}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Draft Section — Teachers Only */}
        {isTeacher && drafts.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-amber-600" />
              <h2 className="font-semibold text-lg text-slate-800">Drafts</h2>
              <Badge variant="outline" className="border-amber-300 text-amber-700">{drafts.length}</Badge>
            </div>

            {drafts.map((release) => (
              <Card key={release.version_id} className="border-amber-200 bg-amber-50/40">
                {editingId === release.version_id ? (
                  <CardContent className="pt-6">
                    <VersionForm
                      data={editForm}
                      onChange={setEditForm}
                      onSubmit={handleSaveEdit}
                      onCancel={() => setEditingId(null)}
                      submitLabel="Save Draft"
                      submitIcon={Save}
                    />
                  </CardContent>
                ) : (
                  <div className="flex">
                    <div className="flex flex-col items-center justify-center gap-1 px-2 border-r border-amber-200 bg-amber-50/60">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                        disabled={versions.indexOf(release) === 0}
                        onClick={() => moveVersion(release.version_id, "up")}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <GripVertical className="w-3 h-3 text-slate-300" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                        disabled={versions.indexOf(release) === versions.length - 1}
                        onClick={() => moveVersion(release.version_id, "down")}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-3">
                              <CardTitle className="text-xl">v{release.version}</CardTitle>
                              {getTypeBadge(release.type)}
                              <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                                <Pencil className="w-3 h-3 mr-1" /> Draft
                              </Badge>
                            </div>
                            <p className="text-slate-600 font-medium">{release.title}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => startEdit(release)} className="text-slate-500 hover:text-blue-600 hover:bg-blue-50">
                              <Pencil className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => { setVersionToPublish(release.version_id); setPublishDialogOpen(true); }}
                            >
                              <Send className="w-4 h-4 mr-1" /> Publish
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-red-600"
                              onClick={() => { setVersionToDelete(release.version_id); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <Separator />
                      <CardContent className="pt-4">
                        <ul className="space-y-2">
                          {(release.changes || []).map((change, i) => (
                            <li key={i} className="flex items-start gap-3">
                              {getTypeIcon(change.type)}
                              <span className="text-slate-700 text-sm">{change.text}</span>
                            </li>
                          ))}
                          {(!release.changes || release.changes.length === 0) && (
                            <li className="text-slate-500 text-sm italic">No changes added yet</li>
                          )}
                        </ul>
                      </CardContent>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Published Versions */}
        {published.length > 0 && (
          <div className="space-y-4">
            {isTeacher && drafts.length > 0 && (
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-600" />
                <h2 className="font-semibold text-lg text-slate-800">Published</h2>
              </div>
            )}

            {published.map((release, index) => (
              <Card key={release.version_id || release.version} className="border-slate-200">
                <div className="flex">
                  {isTeacher && (
                    <div className="flex flex-col items-center justify-center gap-1 px-2 border-r border-slate-100">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                        disabled={versions.indexOf(release) === 0}
                        onClick={() => moveVersion(release.version_id, "up")}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <GripVertical className="w-3 h-3 text-slate-300" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                        disabled={versions.indexOf(release) === versions.length - 1}
                        onClick={() => moveVersion(release.version_id, "down")}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-xl">v{release.version}</CardTitle>
                            {getTypeBadge(release.type)}
                            {index === 0 && (
                              <Badge variant="outline" className="border-green-300 text-green-700">Latest</Badge>
                            )}
                          </div>
                          <p className="text-slate-600 font-medium">{release.title}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {release.published_at
                              ? format(new Date(release.published_at), "MMM d, yyyy")
                              : release.created_at
                              ? format(new Date(release.created_at), "MMM d, yyyy")
                              : ""}
                          </span>
                          {isTeacher && release.version_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setVersionToDelete(release.version_id); setDeleteDialogOpen(true); }}
                              className="h-8 w-8 text-slate-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <Separator />
                    <CardContent className="pt-4">
                      <ul className="space-y-2">
                        {(release.changes || []).map((change, i) => (
                          <li key={i} className="flex items-start gap-3">
                            {getTypeIcon(change.type)}
                            <span className="text-slate-700 text-sm">{change.text}</span>
                          </li>
                        ))}
                        {(!release.changes || release.changes.length === 0) && (
                          <li className="text-slate-500 text-sm">No changes listed</li>
                        )}
                      </ul>
                    </CardContent>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {versions.length === 0 && (
          <Card className="border-slate-100">
            <CardContent className="py-16 text-center">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No version history yet</p>
            </CardContent>
          </Card>
        )}

        {/* Publish Confirmation Dialog */}
        <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Publish this version?</DialogTitle>
              <DialogDescription>
                Once published, this version will be visible to all students. You won't be able to edit it after publishing.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handlePublish}>
                <Send className="w-4 h-4 mr-2" /> Publish
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          title="Delete Version?"
          description="This will permanently delete this version entry. This action cannot be undone."
          confirmText="Delete Version"
        />

        {/* Footer */}
        <div className="text-center py-8 text-slate-500 text-sm">
          <p>Discipleship Intensive • Built with ❤️ for spiritual growth</p>
        </div>
      </div>
    </Layout>
  );
}
